"use client"

import { css, cx } from "@emotion/css"
import { useQuery } from "@tanstack/react-query"
import { ExclamationMessage, Padlock } from "@vectopus/atlas-icons-react"
import { useAtomValue } from "jotai"
import React, { useContext, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import YellowBox from "@/components/course-material/YellowBox"
import { fetchPeerOrSelfReviewDataByExerciseIdOptions } from "@/generated/course-material-api/@tanstack/react-query.generated"
import {
  postFlagAnswerInPeerReview,
  postPeerOrSelfReviewSubmission,
} from "@/generated/course-material-api/sdk.generated"
import type {
  CourseMaterialPeerOrSelfReviewDataWithToken,
  CourseMaterialPeerOrSelfReviewQuestionAnswer,
  ReportReason,
} from "@/generated/course-material-api/types.generated"
import { useUserChapterLocks } from "@/hooks/course-material/useUserChapterLocks"
import PeerReviewProgress from "@/shared-module/common/components/PeerReview/PeerReviewProgress"
import LoginStateContext from "@/shared-module/common/contexts/LoginStateContext"
import useToastMutation from "@/shared-module/common/hooks/useToastMutation"
import { narrowContainerWidthPx } from "@/shared-module/common/styles/constants"
import getGuestPseudonymousUserId from "@/shared-module/common/utils/getGuestPseudonymousUserId"
import { exerciseTaskGradingToExerciseTaskGradingResult } from "@/shared-module/common/utils/typeMappter"
import { Button, LoadingRegion, ErrorNotice } from "@/shared-module/components"
import { courseMaterialAtom } from "@/state/course-material"
import type { Block } from "@/types/courseMaterialBlock"

import type { PeerOrSelfReviewViewProps } from "."
import { getPeerReviewBeginningScrollingId } from "."
import { exerciseButtonStyles, getExerciseBlockBeginningScrollingId } from ".."
import ContentRenderer from "../../.."
import ExerciseTaskIframe from "../ExerciseTaskIframe"
import PeerOrSelfReviewQuestionComponent from "./PeerOrSelfReviewQuestion"
import MarkAsSpamDialog from "./PeerReviewMarkingSpam/MarkAsSpamDialog"
import { reviewerAnswerFields, reviewerDownloadFileName } from "./reviewerAnswer"

const PeerOrSelfReviewViewImpl: React.FC<React.PropsWithChildren<PeerOrSelfReviewViewProps>> = ({
  exerciseNumber,
  exerciseId,
  parentExerciseQuery,
  selfReview,
}) => {
  const { t } = useTranslation()
  const loginStateContext = useContext(LoginStateContext)
  const courseMaterialState = useAtomValue(courseMaterialAtom)
  const [answers, setAnswers] = useState<Map<string, CourseMaterialPeerOrSelfReviewQuestionAnswer>>(
    new Map(),
  )
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false)

  const query = useQuery({
    ...fetchPeerOrSelfReviewDataByExerciseIdOptions({
      path: {
        exercise_id: exerciseId,
      },
    }),
    select: (data): CourseMaterialPeerOrSelfReviewDataWithToken => data,
    // 23 hours in ms. Need to refetch at this time because the given peer review candidate expires in 24 hours, and someone might leave the peer review view open for longer than that
    refetchInterval: 82800000,
  })

  const courseId =
    courseMaterialState.status === "ready" ? (courseMaterialState.course?.id ?? null) : null
  const exerciseData = parentExerciseQuery.data
  const chapterId = exerciseData?.exercise.chapter_id
  const getUserLocks = useUserChapterLocks(courseId)

  const chapterStatus = chapterId
    ? getUserLocks.data?.find((status) => status.chapter_id === chapterId)
    : null
  const isChapterCompleted = Boolean(chapterId && chapterStatus?.status === "completed_and_locked")
  const isChapterNotAccessible = Boolean(chapterId && chapterStatus?.status === "not_unlocked_yet")
  const isChapterLocked: boolean = isChapterCompleted || isChapterNotAccessible

  const peerOrSelfReviewData = query.data?.course_material_peer_or_self_review_data

  const isValid = useMemo(() => {
    if (!peerOrSelfReviewData) {
      return false
    }
    return peerOrSelfReviewData.peer_or_self_review_questions.every((question) => {
      if (!question.answer_required) {
        return true
      }
      const answer = answers.get(question.id)
      if (!answer) {
        return false
      }

      if (answer.number_data !== null) {
        return true
      }

      const textData = answer.text_data ?? ""
      if (textData.trim() !== "") {
        return true
      }

      return false
    })
  }, [answers, peerOrSelfReviewData])

  const submitPeerOrSelfReviewMutation = useToastMutation(
    async () => {
      const token = query.data?.token
      if (!peerOrSelfReviewData || !peerOrSelfReviewData.answer_to_review || !token) {
        return
      }
      return await postPeerOrSelfReviewSubmission({
        path: {
          exercise_id: exerciseId,
        },
        body: {
          exercise_slide_submission_id:
            peerOrSelfReviewData.answer_to_review.exercise_slide_submission_id,
          peer_or_self_review_config_id: peerOrSelfReviewData.peer_or_self_review_config.id,
          peer_review_question_answers: Array.from(answers.values()),
          token,
        },
      })
    },
    { notify: true, method: "POST" },
    {
      onSuccess: async () => {
        // still old data because we have't refetched yet
        const givenEnoughReviews =
          (peerOrSelfReviewData?.peer_or_self_review_config.peer_reviews_to_give ??
            Number.MAX_VALUE) <=
          (peerOrSelfReviewData?.num_peer_reviews_given ?? 0) + 1

        if (givenEnoughReviews || selfReview) {
          await parentExerciseQuery.refetch()
          // Will scroll after once the refetch is complete because the refetch might change the heights of some elements and that would invalidate our current scrolling position
          setTimeout(() => {
            document
              // oxlint-disable-next-line unicorn/prefer-query-selector -- id is a raw UUID; querySelector("#"+id) may throw, getElementById is safe
              .getElementById(getExerciseBlockBeginningScrollingId(exerciseId))
              ?.scrollIntoView({ behavior: "smooth" })
          }, 100)
        }

        // This refetch after the potential exercise refetch because the exercise refetch might close this view and if we refetched this first, we would show an extra intermediate view which would look confusing to the user
        await query.refetch()

        if (!givenEnoughReviews && !selfReview) {
          // Will scroll after once the refetch is complete because the refetch might change the heights of some elements and that would invalidate our current scrolling position
          setTimeout(() => {
            document
              // oxlint-disable-next-line unicorn/prefer-query-selector -- id embeds a raw UUID; "#"+id would need CSS-escaping
              .getElementById(getPeerReviewBeginningScrollingId(exerciseId))

              ?.scrollIntoView({ behavior: "smooth" })
          }, 100)
        }

        setAnswers(new Map())
      },
    },
  )

  const reportMutation = useToastMutation(
    async ({ reason, description }: { reason: ReportReason; description: string }) => {
      if (!peerOrSelfReviewData || !peerOrSelfReviewData.answer_to_review) {
        return
      }
      const token = query.data?.token
      if (!peerOrSelfReviewData || !peerOrSelfReviewData.answer_to_review || !token) {
        return
      }
      return await postFlagAnswerInPeerReview({
        path: {
          exercise_id: exerciseId,
        },
        body: {
          submission_id: peerOrSelfReviewData.answer_to_review.exercise_slide_submission_id,
          reason,
          description,
          flagged_user: null,
          flagged_by: null,
          peer_or_self_review_config_id: peerOrSelfReviewData.peer_or_self_review_config.id,
          token,
        },
      })
    },
    { notify: true, method: "POST" },
    {
      onSuccess: () => {
        setIsReportDialogOpen(false)
        setAnswers(new Map())
        query.refetch()
      },
    },
  )

  const handleReportSubmit = (reason: ReportReason, description: string) => {
    reportMutation.mutate({ reason, description })
  }

  if (query.isError) {
    return <ErrorNotice error={query.error} />
  }

  // Uses isFetching instead of isPending because we want there to be a visual indication when the refresh button is clicked
  if (query.isFetching) {
    return <LoadingRegion />
  }

  if (!query.data) {
    return (
      <div>
        <ErrorNotice error={t("error-loading-exercise")} />
        <button
          className={cx(exerciseButtonStyles)}
          onClick={() => {
            void query.refetch()
          }}
        >
          {t("button-text-try-again")}
        </button>
      </div>
    )
  }

  if (!peerOrSelfReviewData?.answer_to_review?.course_material_exercise_tasks) {
    return (
      <div>
        <div
          className={css`
            margin-bottom: 1rem;
          `}
        >
          {t("help-text-no-answers-to-peer-review-yet")}
        </div>
        <button
          className={cx(exerciseButtonStyles)}
          onClick={() => query.refetch()}
          disabled={query.isLoading}
        >
          {t("button-text-refresh")}
        </button>
      </div>
    )
  }

  return (
    <div
      className={css`
        margin-top: 1rem;
      `}
    >
      {!selfReview && (
        <PeerReviewProgress
          total={peerOrSelfReviewData.peer_or_self_review_config.peer_reviews_to_give}
          attempt={peerOrSelfReviewData.num_peer_reviews_given}
        />
      )}

      {Boolean(peerOrSelfReviewData.peer_or_self_review_config.review_instructions) && (
        <div
          className={css`
            border: 0;
            margin-bottom: 1rem;
            background-color: #fff;
            padding: 0.8rem 1.25rem;
            border-radius: 0.625rem;
          `}
        >
          <h4
            className={css`
              padding-bottom: 0.5rem;
              font-weight: 600;
              font-size: 20px;
            `}
          >
            {selfReview ? t("title-self-review-instructions") : t("title-peer-review-instructions")}
          </h4>

          <ContentRenderer
            data={
              peerOrSelfReviewData.peer_or_self_review_config
                .review_instructions as Block<unknown>[]
            }
            isExam={false}
          />
        </div>
      )}

      <div>
        {peerOrSelfReviewData.answer_to_review.course_material_exercise_tasks
          .toSorted((a, b) => a.order_number - b.order_number)
          .map((course_material_exercise_task) => {
            const answerFields = reviewerAnswerFields(
              course_material_exercise_task.previous_submission,
            )
            return (
              <div key={course_material_exercise_task.id}>
                <div data-testid="assignment">
                  <ContentRenderer
                    data={(course_material_exercise_task.assignment as Block<unknown>[]) ?? []}
                    isExam={false}
                    dontAllowBlockToBeWiderThanContainerWidth={true}
                  />
                </div>

                <ExerciseTaskIframe
                  exerciseTaskId={course_material_exercise_task.id}
                  key={course_material_exercise_task.id}
                  postThisStateToIFrame={{
                    // oxlint-disable-next-line i18next/no-literal-string
                    view_type: "view-submission",
                    exercise_task_id: course_material_exercise_task.id,
                    user_information: {
                      pseudonymous_id:
                        course_material_exercise_task.pseudonumous_user_id ??
                        getGuestPseudonymousUserId(),
                      signed_in: Boolean(loginStateContext.signedIn),
                    },
                    // Don't reveal peer revewiee user variables to peer reviewers in case they contain something sensitive
                    user_variables: {},
                    data: {
                      grading: exerciseTaskGradingToExerciseTaskGradingResult(
                        course_material_exercise_task.previous_submission_grading,
                      ),
                      ...answerFields,
                      public_spec: course_material_exercise_task.public_spec,
                      model_solution_spec: course_material_exercise_task.model_solution_spec,
                    },
                  }}
                  url={`${course_material_exercise_task.exercise_iframe_url}?width=${narrowContainerWidthPx}`}
                  overrideDownloadFilename={(url) =>
                    reviewerDownloadFileName(answerFields.user_answer_files, url)
                  }
                  setAnswer={null}
                  title={t("exercise-task-content", {
                    "exercise-number": exerciseNumber + 1,
                    "task-number": course_material_exercise_task.order_number + 1,
                  })}
                  headingBeforeIframe={
                    selfReview
                      ? t("title-your-answer")
                      : t("title-answer-submitted-by-another-student")
                  }
                />
              </div>
            )
          })}
      </div>

      <hr
        className={css`
          margin-top: 3rem;
          margin-bottom: 2rem;
          background-color: #e0e0e0;
          height: 5px;
          border: none;
        `}
      />

      {peerOrSelfReviewData.peer_or_self_review_questions
        .toSorted((a, b) => a.order_number - b.order_number)
        .map((peerOrSelfReviewQuestion) => (
          <PeerOrSelfReviewQuestionComponent
            key={peerOrSelfReviewQuestion.id}
            peerOrSelfReviewQuestion={peerOrSelfReviewQuestion}
            peerOrSelfReviewQuestionAnswer={answers.get(peerOrSelfReviewQuestion.id) ?? null}
            setPeerOrSelfReviewQuestionAnswer={(newAnswer) => {
              setAnswers((prev) => {
                const nextAnswers = new Map(prev)
                if (
                  newAnswer.number_data === null &&
                  (newAnswer.text_data === null ||
                    newAnswer.text_data === undefined ||
                    newAnswer.text_data.trim() === "")
                ) {
                  // If everything in the answer is null, transform the answer to not answered
                  nextAnswers.delete(peerOrSelfReviewQuestion.id)
                } else {
                  nextAnswers.set(peerOrSelfReviewQuestion.id, {
                    ...newAnswer,
                    peer_or_self_review_question_id: peerOrSelfReviewQuestion.id,
                  })
                }

                return nextAnswers
              })
            }}
          />
        ))}

      {isChapterLocked && (
        <YellowBox>
          <div
            className={css`
              display: flex;
              align-items: center;
              gap: 0.75rem;
            `}
          >
            <Padlock size={24} />
            <div>
              {isChapterNotAccessible
                ? t("chapter-locked-complete-previous")
                : t("chapter-locked-description")}
            </div>
          </div>
        </YellowBox>
      )}
      <button
        className={cx(
          css`
            margin-top: 2.5rem !important;
          `,
          exerciseButtonStyles,
        )}
        disabled={
          !isValid ||
          !peerOrSelfReviewData ||
          submitPeerOrSelfReviewMutation.isPending ||
          isChapterLocked
        }
        onClick={() => submitPeerOrSelfReviewMutation.mutate()}
      >
        {t("submit-button")}
      </button>
      <Button
        className={css`
          display: flex !important;
          align-items: center;
          gap: 6px;
        `}
        variant={"icon"}
        size={"small"}
        onClick={() => setIsReportDialogOpen(true)}
      >
        <ExclamationMessage /> {t("button-text-report")}
      </Button>

      <MarkAsSpamDialog
        isOpen={isReportDialogOpen}
        onClose={() => setIsReportDialogOpen(false)}
        onSubmit={handleReportSubmit}
      />
    </div>
  )
}

export default PeerOrSelfReviewViewImpl
